import {classNames} from '@cohort/shared-frontend/utils/classNames';
import StoreMetaData from '@cohort/wallet/components/store/StoreMetaData';
import {Transition} from '@cohort/wallet/components/transitions/Transition';
import usePageViewTracker from '@cohort/wallet/hooks/pageViewTracker';
import useScreenSize from '@cohort/wallet/hooks/useScreenSize';
import useStoreContext from '@cohort/wallet/hooks/useStoreContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import type {TrackingConfig} from '@cohort/wallet/lib/Tracking';
import {SCROLL_SPY_CONTAINER_ID} from '@cohort/wallet/lib/Utils';
import React, {Fragment, useEffect} from 'react';

type StorePageProps = {
  title: string;
  leftPanel?: React.ReactNode;
  children: React.ReactNode;
  tracking: TrackingConfig;
};

const StorePage = ({title, leftPanel, children, tracking}: StorePageProps): JSX.Element => {
  const {campaign, transitioning, setTransitioning} = useStoreContext();
  const {isMobile} = useScreenSize();
  const {hasDarkBg} = useThemeContext();
  const withLeftPanel = leftPanel && !isMobile;

  usePageViewTracker(tracking.namespace, tracking.metadata);

  useEffect(() => {
    document.title = `${campaign.merchant.name} | ${title}`;
  }, [title, campaign.merchant.name]);

  useEffect(() => {
    setTransitioning(false);
  }, [setTransitioning]);

  return (
    <Fragment>
      <StoreMetaData campaign={campaign} />
      <Transition show={!transitioning} twClasses="h-full w-full overflow-auto" enterFrom="left">
        <div className="mx-auto h-full max-w-[var(--content-max-width)]">
          <div
            className={classNames(
              'grid h-full',
              withLeftPanel
                ? '[grid-template-columns:var(--desktop-left-panel-width)_1fr]'
                : 'grid-cols-1 grid-rows-1'
            )}
            style={{
              gridTemplateAreas: `"leftPanel content"`,
            }}
          >
            {withLeftPanel && (
              <div className="no-scrollbar h-full w-full overflow-auto py-8">
                <div
                  className={classNames(
                    'h-full w-full border-r px-8',
                    hasDarkBg ? 'sm:border-white/20' : 'sm:border-black/20'
                  )}
                >
                  {leftPanel}
                </div>
              </div>
            )}
            <div
              id={SCROLL_SPY_CONTAINER_ID}
              className="no-scrollbar h-full overflow-auto p-0 md:p-8"
            >
              {children}
            </div>
          </div>
        </div>
      </Transition>
    </Fragment>
  );
};

export default StorePage;
